import { Component, ElementRef, OnInit, ViewChild, signal, computed, WritableSignal, inject, effect, viewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe, JsonPipe, NgTemplateOutlet } from '@angular/common';

import { BehaviorSubject, combineLatest, concatMap, from, map, Observable, of, switchMap, tap, throwError, toArray, zip } from 'rxjs';

import {
  faCheckCircle,
  faCircleExclamation,
  faPrint,
  faSpinner,
  faQuestionCircle,
  faUsersBetweenLines,
  faBan,
  faShare,
  faLock,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';

import { ChartConfiguration, ChartData, ChartType } from 'chart.js';

import { BaseChartDirective } from 'ng2-charts';

import { manageSubIntroSteps } from './manage.intro-steps';

import { Student } from '../../../core/models/student.model';
import { SchoolClass } from '../../../core/models/school-class.model';
import { SubscriptionTypes } from '../../../core/models/subscription-types.model';
import { StudentsService } from '../../../core/services/students.service';
import { UtilityService } from '../../../core/services/utility.service';
import { EnrollmentService } from '../../../core/services/enrollment.service';
import { ManagementUser } from '../../../core/models/management-user.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { ReportsService } from '../../../core/services/reports.service';
import { StudentsFilterComponent, StudentsFilterOptions } from '../../../shared/components/students-filter/students-filter.component';
import { PrintService } from 'src/app/core/services/print.service';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { ScoreCutoffs } from '../../../core/models/score-cutoffs.model';
import { AssessmentTypeNames } from '../../../core/models/assessment-types.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SchoolTeacherFilterComponent } from 'src/app/shared/components/school-teacher-filter/school-teacher-filter.component';
import { FormsModule } from '@angular/forms';
import { NgChartsModule } from 'ng2-charts';
import { PercentRoundPipe } from 'src/app/core/pipes/display-percent-rounded-pipe';
import { screenerIntroSteps } from 'src/app/resources/screener.intro-steps';
import { SortColumn, sortUpdate } from 'src/app/core/utils/table';
import { DisplayGradePipe } from 'src/app/core/pipes/display-grade.pipe';
import { StudentDisplay, SubscriptionType } from '../manage-subscription';
import { HIDE_DIAGNOSTIC_SUBSCRIPTIONS } from 'src/app/core/constants/subscription';
import { AssignDiagnosticService, SubscriptionSummary } from 'src/app/core/services/assign-diagnostic.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AllAssessmentsCompletedPipe } from '../all-assessments-completed.pipe';
import { LoadingService } from 'src/app/core/services/loading.service';
import { ManageTeacherSubscriptionsComponent } from '../teacher/manage-teacher-subscriptions.component';
import { RemainingSubscriptionsComponent } from '../remaining/remaining-subscriptions.component';
import { ScreenerResultsComponent } from 'src/app/shared/components/screener-results/screener-results.component';
import { UserRoleGroups, UserRoles } from 'src/app/core/models/user.model';
import { SchoolTeacherFilterOptions } from 'src/app/core/models/filters';
import { MainContentComponent } from 'src/app/shared/components/main-content/main-content.component';
import { TourComponent } from '../../../shared/components/tour/tour.component';
import { AlertComponent } from 'src/app/shared/components/alert/alert.component';
import { TooltipComponent } from 'src/app/shared/components/tooltip/tooltip.component';
import { AbsValuePipe } from 'src/app/shared/pipes/abs-value.pipe';
import { SubscriptionSummaryComponent } from '../summary/subscription-summary.component';
import { groupBy } from 'src/app/shared/utils';

@Component({
  selector: 'wf-manage-student-subscriptions',
  templateUrl: './manage-student-subscriptions.component.html',
  styleUrls: ['./manage-student-subscriptions.component.css'],
  styles: `
    :host {
      display: block;
      height: 100%;
    }
  `,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    NgChartsModule,
    FormsModule,
    SchoolTeacherFilterComponent,
    StudentsFilterComponent,
    FontAwesomeModule,
    PercentRoundPipe,
    DisplayGradePipe,
    DatePipe,
    AllAssessmentsCompletedPipe,
    RemainingSubscriptionsComponent,
    SubscriptionSummaryComponent,
    ScreenerResultsComponent,
    MainContentComponent,
    TourComponent,
    AlertComponent,
    TooltipComponent,
    AbsValuePipe,
  ]
})
export class ManageStudentSubscriptionsComponent implements OnInit {
  private readonly assignDiagnosticService = inject(AssignDiagnosticService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly loadingService = inject(LoadingService);
  private readonly reportService = inject(ReportsService);
  private readonly route = inject(ActivatedRoute);
  private readonly studentService = inject(StudentsService);

  readonly user = inject(SessionStorageService).userData;

  readonly UserRoles = UserRoles;

  readonly HIDE_DIAGNOSTIC_SUBSCRIPTIONS = HIDE_DIAGNOSTIC_SUBSCRIPTIONS;
  readonly COLUMNS = {
    DIAGNOSTIC_SUB: 'Diagnostic',
    FULL_SUB: 'Diagnostic',
    STUDENT_NAME: 'Student Name',
    GRADE: 'Grade',
    SCREENER_SCORE: 'Screener Score',
    SCREENER_STARTED: 'Screener Started',
    SCREENER_COMPLETED: 'Screener Completed',
    SCREENER_PERCENT_COMPLETE: 'Screener Percent Complete',
  } as const;

  readonly tourSteps = manageSubIntroSteps;

  readonly DATE_FORMAT = 'MM/dd/YYYY';

  readonly loading = signal(false);
  readonly saveError = signal(false);

  readonly schoolTeacherFilters = signal<SchoolTeacherFilterOptions | undefined>(undefined);
  readonly filters = signal<StudentsFilterOptions>({
    grade: '',
    class: '',
    extraSupport: ''
  });
  readonly sortColumn = signal<SortColumn>({
    column: this.COLUMNS.STUDENT_NAME,
    reverse: false,
  });

  readonly subscriptionType = signal<"diagnostic" | "system">("system");

  readonly schoolTeacherIDs = computed(() => {
    const user = this.sessionStorageService.userData();
    if (!user) {
      return undefined;
    }

    const schoolTeacherFilters = this.schoolTeacherFilters();
    const filterSchoolID = schoolTeacherFilters?.school?.schoolID;
    const filterTeacherID = schoolTeacherFilters?.teacher?.userID;

    const schoolID = (
      user.userRole === UserRoles.TEACHER
      || user.effectiveUserRole === UserRoles.TEACHER
      || user.userRole === UserRoles.SCHOOL_ADMIN
      || user.effectiveUserRole === UserRoles.SCHOOL_ADMIN
    ) ? user.schoolID : (filterSchoolID ?? 0);

    const teacherID = (
      user.userRole === UserRoles.TEACHER
      || user.effectiveUserRole === UserRoles.TEACHER
    ) ? user.userID : (filterTeacherID ?? 0);

    return {
      schoolID,
      teacherID,
    }
  });

  readonly screenerData = toSignal(
    toObservable(this.schoolTeacherIDs).pipe(
      switchMap((schoolTeacherIDs => {
        if (!schoolTeacherIDs) {
          return of({
            schoolID: 0,
            teacherID: 0,
            grades: [],
            classes: [],
            students: [],
            diagnosticLicenses: 0,
            interventionLicenses: 0,
          });
        }
        return this.reportService
          .getScreenerReportData(schoolTeacherIDs.schoolID, schoolTeacherIDs.teacherID, false)
          .pipe(map(d => ({ ...schoolTeacherIDs, ...d })));
      })
    ))
  );

  // readonly students = signal<Student[]>([]);
  readonly studentsFiltered = computed(() => this.filterStudents());
  readonly studentsFilteredDisplay = computed(() => this.studentsFiltered().map(s => this.studentDisplay(s)));
  readonly studentsFilteredDisplaySorted = computed(() => this.studentsFilteredDisplay().sort((s1, s2) => this.studentDisplayComparator(s1, s2)));

  // If admin user, count subs with NULL/NULL for TeacherID/StudentID, if teacher user, it count of TeacherID/NULL for TeacherID/StudentID

  // TODO: use an httpResource when they are stable
  readonly subscriptionsRefresh = new BehaviorSubject<void>(undefined);
  readonly subscriptions = toSignal(
    combineLatest([
      toObservable(this.user),
      toObservable(this.screenerData),
      this.subscriptionsRefresh
    ]).pipe(
      tap(() => this.loadingService.start()),
      switchMap(([user, screenerData]) => {
        if (!user || !screenerData || !screenerData.students || !screenerData.students.length) {
          return of(undefined);
        }
        const studentIDs = screenerData.students.map((s: any) => s.userID);
        const userID = (
          user.userRole === UserRoles.SCHOOL_ADMIN
          || user.effectiveUserRole === UserRoles.SCHOOL_ADMIN
          || user.userRole === UserRoles.CUSTOMER_SERVICE
        ) ? 0 : user.userID;

        return zip([
          this.assignDiagnosticService.getSubscriptionsForStudents(studentIDs),
          this.assignDiagnosticService.getNumSubscriptionsRemaining(screenerData.schoolID, userID),
          this.getSubscriptionSummary(screenerData.schoolID),
        ]).pipe(map(([students, remaining, summaries]) => ({ remaining, students, summaries }) ));
      }),
      tap(() => this.loadingService.finish()),
    )
  );

  // TODO: Clear this every time filters change?
  // TODO: Make this a linkedSignal and delete the effect
  readonly selectedStudentUserIDs = signal<
    { [sub in "diagnostic" | "system"]: { [userID: number]: boolean } }
    >({
      diagnostic: {},
      system: {}
    });

  readonly anyStudentHasDiagnosticSubs = computed(() => {
    return Object.values(this.subscriptions()?.students ?? {}).some(s => s.diagnostic);
  });

  readonly allAssignableStudentsSelected = computed(() => {
    const subscriptions = this.subscriptions()?.students;
    if (!subscriptions) {
      return false;
    }
    const students = this.studentsFiltered();
    if (students.length === 0) {
      return false;
    }
    const selectedStudentUserIDs = this.selectedStudentUserIDs();
    const subscriptionType = this.subscriptionType();

    return students.filter(
      s => !subscriptions[s.userID]?.[subscriptionType]?.consumed && !selectedStudentUserIDs[subscriptionType][s.userID]
    ).length === 0;
  });

  protected readonly populateSelectedStudentUserIDsEffect = effect(() => this.resetSelection());

  readonly studentsNotStarted = computed(() =>
    this.studentsFiltered()
      .filter(s => s.firstAssessmentScore && s.firstAssessmentScore.isActive && !s.firstAssessmentScore.dateStarted).length
  );
  readonly studentsSkipped = computed(() =>
    this.studentsFiltered()
      .filter(s => s.firstAssessmentScore && !s.firstAssessmentScore.isActive && !s.firstAssessmentScore.dateCompleted).length
  );
  readonly studentsStarted = computed(() =>
    this.studentsFiltered().length - this.studentsNotStarted() - this.studentsSkipped()
  );

  readonly showDiagnosticSubscriptions = computed(() =>
    this.subscriptions()?.remaining?.diagnostic === -1 || (this.subscriptions()?.remaining?.diagnostic ?? 0) > 0 || this.anyStudentHasDiagnosticSubs()
  );
  protected readonly showDiagnosticSubscriptionsChangeEffect = effect(() => {
    // If we switch schools to one that shouldn't show diagnostic, then switch back to system
    if (!this.showDiagnosticSubscriptions()) {
      this.subscriptionType.set("system");
    }
  });

  readonly notStartedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsNotStarted() / this.filterStudents().length) * 100) : 0 ) ;
  readonly startedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsStarted() / this.filterStudents().length) * 100) : 0 );
  readonly skippedPercentage = computed(() => this.filterStudents().length > 0 ? Math.round((this.studentsSkipped() / this.filterStudents().length) * 100) : 0 );

  readonly tierLabels = computed(() => {
    const filterOpts = this.filters();
    // Grade value that comes from the filter is our true, displayed grade (not our internal grade, so 4 is 4th grade)
    let gradeNum = parseInt(filterOpts.grade) ;
    let selectedGradeGroup = (filterOpts.grade === 'four_below_all' || (!isNaN(gradeNum) && gradeNum <= 4)) ? 'elementary' : 'secondary' ;
    if (filterOpts.grade === 'all')
    {
      return  {
        'proficient' : '',
        'some-risk' : '',
        'high-risk' : '',
      } ;
    }
    return  {
      'proficient' : ScoreCutoffs[selectedGradeGroup]['labels']['topTier']['range'],
      'some-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['middleTier']['range'],
      'high-risk' : ScoreCutoffs[selectedGradeGroup]['labels']['bottomTier']['range'],
    } ;
  })

  toggleSub(level: 'diagnostic' | 'system', student: StudentDisplay) {
    const studentID = student.userID;
    this.selectedStudentUserIDs.update(s => {
      s[level][studentID] = !(s[level][studentID] ?? false);
      return { ...s };
    })
  }

  showSchoolFilter: boolean = false ;
  showConfirmDialog: boolean = false ;
  confirmSkipScreenerTo: string = '' ;
  schoolFilterError: string = '' ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  studentsAreSelected: boolean = false ;
  readonly grades = signal<string[]>([]);
  readonly classes = signal<SchoolClass[]>([]);
  printIcon = faPrint ;
  loadingIcon = faSpinner ;
  lockIcon = faLock;
  readonly enrollIcon = faUserPlus;
  diagnosticTitle = SubscriptionTypes.DiagnosticProduct ;
  systemTitle = SubscriptionTypes.FullProduct ;

  tableData: any[] = [] ;
  sortReverse: boolean = false ;
  message: string = '' ;
  messageTitle: string = '' ;
  messageStatus: string = '' ;
  successIcon = faCheckCircle ;
  errorIcon = faCircleExclamation ;

  studentScoreRange: string = '' ;
  processingDiag: boolean = false ;
  processingFull: boolean = false ;
  helpIcon = faQuestionCircle;
  coTeacherIcon = faUsersBetweenLines;
  introTour: any;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective ;
  @ViewChild(StudentsFilterComponent) studentFilter?: StudentsFilterComponent ;
  @ViewChild('screenerSummaryCanvas') screenerSummaryCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('screenerSummaryImg') screenerSummaryImg!: ElementRef<HTMLImageElement> ;
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;

  ngOnInit(): void {
    const user = this.user();
    // Filter data
    const filterData = this.route.snapshot.data['filterData'];
    this.districts = filterData.districts;
    this.schools = filterData.schools;
    this.teachers = user?.userRole === UserRoles.CUSTOMER_SERVICE ? [] : filterData.teachers.filter((t: any) => t.userID) // filter out the 'All' option ;

    if (this.route.snapshot.data['resolveData'].erred)
    {
      this.schoolFilterError = this.route.snapshot.data['resolveData'].message ;
    }
    else
    {
      this.grades.set(this.route.snapshot.data['resolveData'].grades);
      this.classes.set(this.route.snapshot.data['resolveData'].classes);
    }

    if (user?.isFILUser() || user?.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }
  }

  ngOnDestroy(): void {
    this.loadingService.finish();
  }

  updateSort(clickedColumn: string) {
    this.sortColumn.update(sortUpdate(clickedColumn));
  }

  filterStudents() {
    // TODO: Improve error handling when httpResource is available
    const students = (this.screenerData() as any)?.students;
    if (!students) {
      return [];
    }
    return this.studentService.filterStudents(students, this.filters()) ;
  }

  // Asserts if a student can be selected by the (teacher) user.
  // Students they've been given report access to cannot be selected, only students
  // that are explicitly assigned to them.
  isPrimaryTeacherForStudent(student: Student) {
    const selectedTeacherId = this.getTeacherIdForAssignment();
    if (!selectedTeacherId) {
      return false;
    }

    return selectedTeacherId === student.teacherID;
  }

  canSelectStudentsForAssignment() {
    return this.getTeacherIdForAssignment() > 0;
  }

  private getTeacherIdForAssignment(): number {
    const user = this.user();
    if (user?.isTeacherUser()) {
      return user.userID;
    }

    if (user?.isSchoolAdmin() || user?.isCustomerServiceUser()) {
      return this.reportService.getSelectedTeacherForReports().userID;
    }

    return 0;
  }

  selectAll() {
    const students = this.studentsFiltered();
    const subs = this.subscriptions()?.students;
    if (!students || !subs) {
      return;
    }
    this.selectedStudentUserIDs.update(selected => {
      const type = this.subscriptionType();
      const newSelected: { [studentID: number]: boolean } = {};
      for (const student of students) {
        const diagnosticSub = subs[student.userID]?.diagnostic;
        const systemSub = subs[student.userID]?.system;
        // Select if there no existing subscription or there is but it isn't consumed yet.
        // For diagnostic there is an additional check to make sure they don't already have a system sub
        if (
          (type === "diagnostic" && (!diagnosticSub || !diagnosticSub.consumed) && !systemSub)
          || (type === "system" && (!systemSub || !systemSub.consumed))
        ) {
          newSelected[student.userID] = true;
        }
      }
      selected[type] = newSelected;
      return { ...selected };
    });
  }

  selectNone() {
    this.selectedStudentUserIDs.update(selected => {
      return {
        ...selected,
        [this.subscriptionType()]: {}
      };
    });
  }

  resetSelection() {
    const subscriptions = this.subscriptions();
    if (!subscriptions) {
      return;
    }
    const studentSubscriptions = subscriptions.students;

    const studentsWithNonConsumedDiagnosticSub: { [userID: number]: boolean } = {};
    Object.keys(studentSubscriptions).filter(id => {
      const sub = studentSubscriptions[parseInt(id)]?.diagnostic;
      return sub && !sub.consumed;
    }).forEach(id => { studentsWithNonConsumedDiagnosticSub[parseInt(id)] = true; });

    const studentsWithNonConsumedSystemSub: { [userID: number]: boolean } = {};
    Object.keys(studentSubscriptions).filter(id => {
      const sub = studentSubscriptions[parseInt(id)]?.system;
      return sub && !sub.consumed;
    }).forEach(id => { studentsWithNonConsumedSystemSub[parseInt(id)] = true; });

    this.selectedStudentUserIDs.set({
      diagnostic: studentsWithNonConsumedDiagnosticSub,
      system: studentsWithNonConsumedSystemSub
    });
  }

  readonly subscriptionDiff = computed(
    () => {
      const students = this.studentsFiltered();
      const selectedStudentUserIDs = this.selectedStudentUserIDs();
      const subscriptionType = this.subscriptionType();

      const remove = students.filter(s => {
        const sub = this.subscriptions()?.students?.[s.userID]?.[subscriptionType];
        // if they don't have a sub there's nothing to remove. likewise if they have a sub but it's consumed they can't be removed
        if (!sub || sub.consumed) {
          return false;
        }
        const isSelected = selectedStudentUserIDs[subscriptionType][s.userID];
        // if they have a sub but are unchecked in the ui, they should be removed
        return !isSelected;
      });

      const add = students.filter(s => {
        const sub = this.subscriptions()?.students?.[s.userID]?.[subscriptionType];
        // if they have a sub, they can't be added
        if (sub) {
          return false;
        }
        const isSelected = selectedStudentUserIDs[subscriptionType][s.userID];
        // if they don't have a sub already and are checked
        return isSelected;
      });

      return {
        remove,
        add,
      };
    }
  );

  save() {
    const { remove, add } = this.subscriptionDiff();
    const subscriptionType = this.subscriptionType();

    const remove$ = from(Object.entries(groupBy(remove, s => s.teacherID))).pipe(
      concatMap(([teacherID, removeStudents]) =>
        subscriptionType === 'diagnostic'
          ? this.assignDiagnosticService.removeDiagnosticFromStudents(removeStudents.map((s: Student) => s.userID), parseInt(teacherID))
          : this.assignDiagnosticService.removeFullProductFromStudents(removeStudents.map((s: Student) => s.userID), parseInt(teacherID))
      ),
      toArray(), //combine all requests into one value emission
    );

    const add$ = from(Object.entries(groupBy(add, s => s.teacherID))).pipe(
      concatMap(([teacherID, addStudents]) =>
        subscriptionType === 'diagnostic'
          ? this.assignDiagnosticService.assignDiagnosticToStudents(addStudents.map((s: Student) => s.userID), parseInt(teacherID))
          : this.assignDiagnosticService.assignFullProductToStudents(addStudents.map((s: Student) => s.userID), parseInt(teacherID))
      ),
      toArray(), //combine all requests into one value emission
    );

    this.loadingService.start();
    const error = () => {
      this.loadingService.finish();
      this.saveError.set(true);
    };

    // Do remove first to free up any subs before add potentially needs them
    remove$.subscribe({
      next: () => add$.subscribe({
        next: () => {
          this.subscriptionsRefresh.next();
          this.loadingService.finish();
        },
        error,
      }),
      error,
    });
  }

  private studentDisplay(student: Student): StudentDisplay {
    // This is ported over from the legacy app, but this logic makes it appear that
    // we are iterating over all assessments, so if there was more than one sent back
    // we could be counting extra (more than 1 assessment per student) -- however, it
    // looks like when the call to `/screenerreports` is made, the query returns just
    // the Screener data, so a single assessment
    let studentGroup = (student.grade <= 5) ? 'elementary' : 'secondary' ;

    // Set our screener results class
    // FUTURE: Could this be moved to the assessment score model as a method?
    let screenerClass = '' ;
    if (student.firstAssessmentScore && student.firstAssessmentScore.dateCompleted)
    {
      if (student.firstAssessmentScore.systemScore < ScoreCutoffs[studentGroup]['automaticity']['some-risk'])
      {
        screenerClass = 'score-red' ;
      }
      else if (student.firstAssessmentScore.systemScore < ScoreCutoffs[studentGroup]['automaticity']['proficient'])
      {
        screenerClass = 'score-yellow' ;
      }
      else
      {
        screenerClass = 'score-green' ;
      }
    }

    const scoreRange =  (!student.firstAssessmentScore || student.firstAssessmentScore?.screenerResultsLow < 0 || student.firstAssessmentScore!.screenerResultsHigh < 0)
      ? '0'
      : `${student.firstAssessmentScore!.screenerResultsLow} - ${student.firstAssessmentScore!.screenerResultsHigh}`;

    // Push to our tableData array, which contains simplified (and thus sortable) data to display
    let percentComplete = 0 ;
    let skipped = false ;
    if (student.firstAssessmentScore && student.firstAssessmentScore.assessmentType === AssessmentTypeNames.SCREENER)
    {
      skipped = !student.firstAssessmentScore.dateCompleted && !student.firstAssessmentScore.isActive ;
      if (!student.firstAssessmentScore.numDiagnosticTasks)
      {
        percentComplete = 0 ;
      }
      else
      {
        percentComplete = (student.firstAssessmentScore.numDiagnosticTasksCompleted / student.firstAssessmentScore.numDiagnosticTasks) * 100 ;
      }
    }

    return {
      ...student,
      scoreRange,
      percentComplete,
      skipped,
      screenerClass
    };
  }

  private studentDisplayComparator(s1: StudentDisplay, s2: StudentDisplay) {
    const { column, reverse } = this.sortColumn();

    const nameComp = s1.firstName.localeCompare(s2.firstName) || s1.lastName.localeCompare(s2.lastName);

    let comp = nameComp;
    if (column === this.COLUMNS.GRADE) {
      comp = s1.grade - s2.grade;
    } else if (column === this.COLUMNS.SCREENER_SCORE) {
      comp = (s1.firstAssessmentScore?.screenerResultsLow ?? 0) - (s2.firstAssessmentScore?.screenerResultsLow ?? 0);
    } else if (column === this.COLUMNS.SCREENER_STARTED) {
      comp = (s1.firstAssessmentScore?.dateStarted ?? 0) - (s2.firstAssessmentScore?.dateStarted ?? 0);
    } else if (column === this.COLUMNS.SCREENER_COMPLETED) {
      comp = (s1.firstAssessmentScore?.dateCompleted ?? 0) - (s2.firstAssessmentScore?.dateCompleted ?? 0);
    } else if (column === this.COLUMNS.SCREENER_PERCENT_COMPLETE) {
      comp = s1.percentComplete - s2.percentComplete;
    }

    if (comp === 0 && column !== this.COLUMNS.STUDENT_NAME) {
      // On tie of non-name columns, fall back to ascending name
      // This ignores reverse. Reverse should only affect the top level sort, not the tiebreaker.
      return nameComp;
    }

    return reverse ? (comp * -1) : comp
  }

  protected readonly cancelIcon = faBan;
  protected readonly skipIcon = faShare;

  private getSubscriptionSummary(schoolID: number): Observable<{ diagnostic: SubscriptionSummary, system: SubscriptionSummary } | undefined> {
    if (
      this.user()?.userRole === UserRoles.CUSTOMER_SERVICE
      || this.user()?.userRole === UserRoles.SCHOOL_ADMIN
      || this.user()?.effectiveUserRole === UserRoles.SCHOOL_ADMIN
    ) {
      return this.assignDiagnosticService.getSchoolSubscriptionSummary(schoolID);
    }
    return of(undefined);
  }
}
